import React, { createContext, useContext, useState } from "react"
import { MobergRow, MobergColumn } from "../../../../../Moberg"
import { SourceDataLineModalities } from "./SourceDataLineModalities"
import { AnalyticLineModalities } from "./AnalyticLineModalities"
import { DeltaModalities } from "./DeltaModalities"
import { CompositePartTraceConfigJSON, LineTraceConfig, LineTraceConfigJSON, RenderStrategy, TraceConfigJSON, UpdateTraceOptions } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { TabCount } from "./TabCount"
import { applyModalitySpecificDefaults } from "../../../../../Constants/LabelSettings"
import { ModalityGraphConfigJSON } from "../../../../../Pages/Data/Visualize/DataReview/Types/Graph"
import { extent } from "d3"

type LineGraphModalitiesContextValue = {
	activeSourceTraces: LineTraceConfig[]
	activeAnalyticTraces: LineTraceConfig[]
	updateTrace: (id: string, newValue: LineTraceConfigJSON, options?: UpdateTraceOptions) => void
	handleColorChange: (traceId: string, color: string) => void
}

export const LineGraphModalitiesContext = createContext<LineGraphModalitiesContextValue>({} as LineGraphModalitiesContextValue)

export const LineGraphModalities: React.FC = () => {
	const { activeSourceTraces, activeAnalyticTraces, currentGraph, updateGraphProperty, updateGraph } = useContext(ConfigureWindowModalContext)
	const tabs = ["Source data", "Analytics", "Delta regions"]
	const [selectedTab, setSelectedTab] = useState(tabs[0])

	const TabContent = () => {
		switch(selectedTab) {
			case tabs[0]:
				return <SourceDataLineModalities />
			case tabs[1]:
				return <AnalyticLineModalities />
			case tabs[2]:
				return <DeltaModalities />
			default:
				throw new Error("Tab section not implemented! " + selectedTab)
		}
	}

	const updateTrace = (traceId: string, config: LineTraceConfigJSON, options?: UpdateTraceOptions) => {
		updateGraph((previousGraph: ModalityGraphConfigJSON) => {
			let graphMin = previousGraph.autoScale ? previousGraph.min : previousGraph.defaultMin
			let graphMax = previousGraph.autoScale ? previousGraph.max : previousGraph.defaultMax
			let autoScale = previousGraph.autoScale
			let newConfig: LineTraceConfigJSON

			if (options?.useModalitySpecificDefaults) {
				newConfig = applyModalitySpecificDefaults(config, config.rawDataKey, RenderStrategy.LINE)
				const { min, max, autoScale: shouldAutoScale } = getNewGraphProperties(previousGraph, newConfig, traceId)
				graphMin = min ?? graphMin
				graphMax = max ?? graphMax
				autoScale = shouldAutoScale
			} else {
				newConfig = { ...config }
			}

			const newTraces: TraceConfigJSON[] = getNewTraces(previousGraph, newConfig, traceId)

			return {
				...previousGraph,
				traces: newTraces,
				min: graphMin,
				defaultMin: graphMin,
				max: graphMax,
				defaultMax: graphMax,
				autoScale
			}
		})
	}

	const getNewGraphProperties = (previousGraph: ModalityGraphConfigJSON, newConfig: LineTraceConfigJSON, traceId: string) => {
		const allTracesWithDefaults = previousGraph.traces
			.filter(trace => trace.renderStrategy !== RenderStrategy.DELTA)
			.map(trace => {
				let mutableTrace = { ...trace } as LineTraceConfig

				if (trace.id === traceId) {
					mutableTrace = newConfig as LineTraceConfig
				}

				return applyModalitySpecificDefaults<LineTraceConfig>(mutableTrace, mutableTrace.rawDataKey, RenderStrategy.LINE)
			})

		const allTraceMinMax = allTracesWithDefaults
			.flatMap(traceWithDefaults => ([traceWithDefaults.min, traceWithDefaults.max]))
			.filter(value => value !== undefined) as number[]

		const autoscales = allTracesWithDefaults
			.map(trace => trace.autoscale)
			.filter(value => value !== undefined)

		const autoScale = autoscales.length === 0 || autoscales.some(value => value)
		
		const graphExtent = extent(allTraceMinMax)

		return { min: graphExtent[0], max: graphExtent[1], autoScale }
	}

	const getNewTraces = (previousGraph: ModalityGraphConfigJSON, newConfig: LineTraceConfigJSON, traceId: string) => {
		const defaults = { isVisible: true }

		return previousGraph.traces.map(trace => {
			const newTrace = {
				...defaults,
				...trace, 
				...newConfig,
				id: traceId // Always important to make sure that the trace ID remains
			}

			if (newConfig.isCompositePart && newConfig.compositeIndex !== undefined) {
				(newTrace as CompositePartTraceConfigJSON).compositeIndex = newConfig.compositeIndex
			}
	
			// This optional property should be removed if the the modality changed and the new trace does not have an analysis.
			if (newConfig.name && !newConfig.onDemandAnalysis) {
				newTrace.onDemandAnalysis = undefined
			}

			switch (trace.renderStrategy) {
				case RenderStrategy.DELTA:
					// If the trace is updated and a delta region uses it, update its de-normalized copy.
					const delta = { ...trace }

					if (traceId === trace.first.id) {
						delta.first = {...newConfig, id: traceId } as any
					}

					if (traceId === trace.second.id) {
						delta.second = {...newConfig, id: traceId } as any
					}

					return delta
					
				default:
					if (traceId === trace.id) {
						return newTrace
					} else {
						return trace
					}
			}
		})
	}

	const handleColorChange = (traceId: string, color: string) => {
		const newTraces = [...(currentGraph?.traces ?? [])].map(trace => {
			if (trace.id === traceId) {
				return {
					...trace,
					color
				}
			}
			return trace
		})

		updateGraphProperty("traces", newTraces)
	}

	return (
		<MobergColumn gap="16px">
			<div style={{ borderBottom: '1px solid var(--color-border-light)' }}>
				<MobergRow gap="24px" style={{ padding: '0 4px' }}>
					{tabs.map(tab => (
						<div
							key={tab}
							onClick={() => setSelectedTab(tab)}
							style={{
								cursor: 'pointer',
								padding: '8px 4px',
								fontWeight: tab === selectedTab ? 600 : 400,
								color: tab === selectedTab ? 'var(--color-accent-interactive)' : 'var(--text-secondary-color)',
								borderBottom: tab === selectedTab ? '2px solid var(--color-accent-interactive)' : '2px solid transparent',
								transition: 'color 0.15s, border-bottom 0.15s',
								fontSize: 14,
								background: 'none',
								outline: 'none',
								display: 'flex',
								alignItems: 'center',
								gap: '6px',
								marginBottom: '-1px',
							}}
						>
							<span>{tab}</span>
							<TabCount tab={tab} />
						</div>
					))}
				</MobergRow>
			</div>

			<LineGraphModalitiesContext.Provider value={{
				activeSourceTraces: activeSourceTraces as LineTraceConfig[],
				activeAnalyticTraces: activeAnalyticTraces as LineTraceConfig[],
				updateTrace, handleColorChange }}>
				{TabContent()}
			</LineGraphModalitiesContext.Provider>
		</MobergColumn>
	)
}
